import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  useAnnualOccasionalsQuery,
  useLatestExpensesQuery,
} from '../features/expenses/queries';
import { useEmergencyFundAlertsQuery } from '../features/emergencyFund/queries';
import {
  useBudgetAlertsQuery,
  useDismissBudgetAlertMutation,
} from '../features/budgets/queries';
import { usePriorityGoalQuery } from '../features/goals/queries';
import { useSnapshotsTimelineQuery } from '../features/snapshots/queries';
import { useSelfieCycle } from '../store/SelfieCycleContext';
import { useAuth } from '../store/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useCycleFinancialOverview } from '../hooks/useCycleFinancialOverview';
import { buildDashboardAlerts, DASHBOARD_ALERT_TYPES } from '../utils/dashboardAlerts';
import { dismissAlertId } from '../utils/dismissedAlerts';
import { AlertsPanel } from '../components/alerts/AlertsPanel';
import { DashboardCycleHeader } from '../components/dashboard/DashboardCycleHeader';
import {
  DashboardGuidedFlowHero,
  useGuidedFlowDashboardState,
} from '../components/dashboard/DashboardGuidedFlowHero';
import { GuidedFlowWizardModal } from '../components/dashboard/GuidedFlowWizardModal';
import { guidedFlowKeys } from '../features/guidedFlow/queries';
import { DashboardNetWorthHero } from '../components/dashboard/DashboardNetWorthHero';
import { DashboardCycleSummary } from '../components/dashboard/DashboardCycleSummary';
import { ExpenseTimelineMiniCard } from '../components/dashboard/ExpenseTimelineMiniCard';
import { GoalFocusCard } from '../components/dashboard/GoalFocusCard';
import { AnnualAgendaCard } from '../components/dashboard/AnnualAgendaCard';
import { SelfieCadenceCard } from '../components/dashboard/SelfieCadenceCard';
import { ExpenseRecentCard } from '../components/dashboard/ExpenseRecentCard';
import { CapitalDistributionMiniCard } from '../components/dashboard/CapitalDistributionMiniCard';
import { PageContentShell } from '../components/common/PageContentShell';
import { SectionSkeleton } from '../components/common/SectionSkeleton';
import { useQueryShellState } from '../hooks/useQueryShellState';

function TruthZone({
  showNetWorth,
  patrimonioTimeline,
  timelineError,
  onRetryTimeline,
  onOpenGuidedWizard,
}) {
  if (showNetWorth) {
    return (
      <DashboardNetWorthHero
        timeline={patrimonioTimeline}
        isError={timelineError}
        onRetry={onRetryTimeline}
      />
    );
  }
  return <DashboardGuidedFlowHero onOpenWizard={onOpenGuidedWizard} />;
}

const OPEN_GUIDED_FLOW_PARAM = 'openGuidedFlow';

function DashboardContentFallback() {
  return (
    <div className="flex flex-col gap-3" data-testid="dashboard-content-skeleton">
      <SectionSkeleton variant="hero" className="min-h-[8rem]" />
      <SectionSkeleton variant="card-grid" className="sm:grid-cols-2" />
    </div>
  );
}

export function Dashboard() {
  const { profile } = useAuth();
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const layout = isLg ? 'desktop' : isMd ? 'tablet' : 'mobile';

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const { currentCycle: activeCycle, isLoading: cyclesLoading } = useSelfieCycle();
  const { finished: guidedFlowFinished, dismissed: guidedFlowDismissed } =
    useGuidedFlowDashboardState();

  // Dono do modal da jornada guiada (não do cartão — ver DashboardGuidedFlowHero.jsx):
  // precisa de sobreviver à troca de cartão quando o último passo conclui a meio
  // da celebração.
  const [guidedWizardOpen, setGuidedWizardOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(OPEN_GUIDED_FLOW_PARAM) !== '1') return;

    setGuidedWizardOpen(true);
    params.delete(OPEN_GUIDED_FLOW_PARAM);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  const guidedFlowModal = (
    <GuidedFlowWizardModal
      open={guidedWizardOpen}
      onOpenChange={(open) => {
        setGuidedWizardOpen(open);
        if (!open) {
          void qc.invalidateQueries({ queryKey: guidedFlowKeys.all });
        }
      }}
    />
  );

  const cycleExpenseFilters = useMemo(() => {
    if (activeCycle?.start && activeCycle?.end) {
      return {
        occurred_on_from: activeCycle.start,
        occurred_on_to: activeCycle.end,
      };
    }
    return { year, month };
  }, [activeCycle, year, month]);

  const { data: latestExpenses = [] } = useLatestExpensesQuery(cycleExpenseFilters);
  const { data: annualRows = [] } = useAnnualOccasionalsQuery();

  const {
    data: timelineData,
    isPending: timelinePending,
    isFetching: timelineFetching,
    isError: timelineIsError,
    refetch: refetchTimeline,
  } = useSnapshotsTimelineQuery();
  const patrimonioTimeline = useMemo(() => timelineData ?? [], [timelineData]);

  const timelineShell = useQueryShellState({
    data: timelineData,
    isPending: timelinePending,
    isFetching: timelineFetching,
  });

  const {
    overview,
    monthlySummary,
    overviewQuery,
    monthlySummaryQuery,
    isPending: overviewPending,
    isFetching: overviewFetching,
  } = useCycleFinancialOverview(activeCycle);

  /** `.pen` → bloco `error` de ds/card/cycle-summary e ds/card/networth-hero. */
  const cycleIsError = Boolean(overviewQuery?.isError || monthlySummaryQuery?.isError);
  const retryCycle = () => {
    overviewQuery?.refetch();
    monthlySummaryQuery?.refetch();
  };
  const timelineError = timelineIsError && patrimonioTimeline.length === 0;

  const overviewShell = useQueryShellState({
    data: activeCycle ? overview : undefined,
    isPending: cyclesLoading || (Boolean(activeCycle) && overviewPending),
    isFetching: Boolean(activeCycle) && overviewFetching,
  });

  const { data: priorityGoal } = usePriorityGoalQuery();
  const { data: efApiAlerts = [] } = useEmergencyFundAlertsQuery();
  const { data: budgetAlerts = [] } = useBudgetAlertsQuery({ is_dismissed: false });
  const dismissBudgetAlertMutation = useDismissBudgetAlertMutation();

  const dashboardAlerts = useMemo(
    () =>
      buildDashboardAlerts(overview, {
        hasIncome: Number(monthlySummary?.income?.total || 0) > 0,
        hasSnapshots: patrimonioTimeline.length > 0,
        efApiAlerts,
        budgetAlerts,
      }),
    [overview, monthlySummary, patrimonioTimeline, efApiAlerts, budgetAlerts],
  );

  const onDismissDashboardAlert = (alert) => {
    if (alert.type === DASHBOARD_ALERT_TYPES.CATEGORY_BUDGET_EXCEEDED_SUMMARY) {
      return Promise.all(
        (alert.meta.alertIds || []).map((id) =>
          dismissBudgetAlertMutation.mutateAsync(id),
        ),
      );
    }
    dismissAlertId(alert.id);
    return undefined;
  };

  const latestCompletedSnapshot = useMemo(() => {
    const completed = patrimonioTimeline.filter(
      (snapshot) => snapshot.is_completed !== false && snapshot.snapshot_date,
    );
    return completed.length ? completed[completed.length - 1] : null;
  }, [patrimonioTimeline]);

  const hasPatrimonioData = patrimonioTimeline.length >= 1;
  const showNetWorth = guidedFlowFinished || hasPatrimonioData || guidedFlowDismissed;
  const showGuidedInMore = !guidedFlowFinished && hasPatrimonioData && !guidedFlowDismissed;
  const showGoal = guidedFlowFinished && priorityGoal;
  const showAnnual =
    profile?.annual_calendar_reminders_enabled !== false && annualRows.length > 0;
  const showExpenses = latestExpenses.length > 0;

  const primaryShells = [overviewShell, timelineShell];
  const remindersEnabled = profile?.selfie_notifications_enabled !== false;

  /** Ordem de leitura: alertas → ciclo → ainda podes gastar → últimas despesas →
   * pagamentos anuais → evolução das despesas → patrimônio → distribuição do
   * capital → foco de objetivo → selfie financeira. */
  const cycleHeaderCard = (
    <DashboardCycleHeader cycle={activeCycle} remindersEnabled={remindersEnabled} />
  );
  const cycleSummaryCard = (
    <DashboardCycleSummary
      overview={overview}
      monthlySummary={monthlySummary}
      cycle={activeCycle}
      isError={cycleIsError}
      onRetry={retryCycle}
    />
  );
  const expenseRecentCard = showExpenses && <ExpenseRecentCard expenses={latestExpenses} />;
  const annualAgendaCard = showAnnual && (
    <AnnualAgendaCard items={annualRows} referenceYear={year} currentMonth={month} />
  );
  const truthZoneCard = (
    <TruthZone
      showNetWorth={showNetWorth}
      patrimonioTimeline={patrimonioTimeline}
      timelineError={timelineError}
      onRetryTimeline={refetchTimeline}
      onOpenGuidedWizard={() => setGuidedWizardOpen(true)}
    />
  );
  const goalFocusCard = (showGuidedInMore || showGoal) && (
    <>
      {showGuidedInMore && (
        <DashboardGuidedFlowHero onOpenWizard={() => setGuidedWizardOpen(true)} />
      )}
      {showGoal && <GoalFocusCard goal={priorityGoal} />}
    </>
  );
  const selfieCard = (
    <SelfieCadenceCard timeline={patrimonioTimeline} latestSnapshot={latestCompletedSnapshot} />
  );

  const StackedContent = (
    <>
      {cycleHeaderCard}
      {cycleSummaryCard}
      {expenseRecentCard}
      {annualAgendaCard}
      <ExpenseTimelineMiniCard />
      {truthZoneCard}
      <CapitalDistributionMiniCard />
      {goalFocusCard}
      {selfieCard}
    </>
  );

  if (layout === 'desktop') {
    return (
      <>
      <div
        className="flex w-full min-w-0 flex-col gap-5 overflow-x-hidden pb-2"
        data-testid="dashboard-desktop"
      >
        <PageContentShell
          shells={primaryShells}
          fallback={<DashboardContentFallback />}
          className="flex flex-col gap-5"
        >
          <AlertsPanel alerts={dashboardAlerts} onCustomDismiss={onDismissDashboardAlert} />
          {cycleHeaderCard}
          <div className="grid grid-cols-2 items-start gap-5">
            {cycleSummaryCard}
            <div className="flex flex-col gap-4">{expenseRecentCard}</div>
          </div>
          <div className="grid grid-cols-2 items-start gap-5">
            <div className="flex flex-col gap-4">{annualAgendaCard}</div>
            <ExpenseTimelineMiniCard />
          </div>
          <div className="grid grid-cols-[1.55fr_1fr] items-start gap-5">
            {truthZoneCard}
            <CapitalDistributionMiniCard />
          </div>
          <div className="grid grid-cols-2 items-start gap-5">
            <div className="flex flex-col gap-4">{goalFocusCard}</div>
            <div className="flex flex-col gap-4">{selfieCard}</div>
          </div>
        </PageContentShell>
      </div>
      {guidedFlowModal}
      </>
    );
  }

  if (layout === 'tablet') {
    return (
      <>
      <div
        className="flex w-full min-w-0 flex-col gap-5 overflow-x-hidden pb-2"
        data-testid="dashboard-tablet"
      >
        <PageContentShell
          shells={primaryShells}
          fallback={<DashboardContentFallback />}
          className="flex flex-col gap-5"
        >
          <AlertsPanel alerts={dashboardAlerts} onCustomDismiss={onDismissDashboardAlert} />
          {StackedContent}
        </PageContentShell>
      </div>
      {guidedFlowModal}
      </>
    );
  }

  return (
    <>
    <div className="flex w-full min-w-0 flex-col gap-5 overflow-x-hidden pb-2">
      <PageContentShell
        shells={primaryShells}
        fallback={<DashboardContentFallback />}
        className="flex flex-col gap-5"
      >
        <AlertsPanel alerts={dashboardAlerts} onCustomDismiss={onDismissDashboardAlert} />
        {StackedContent}
      </PageContentShell>
    </div>
    {guidedFlowModal}
    </>
  );
}
